import { HasPermission, IsStudent } from "../../../helper/helperFunction";
import { Link } from "react-router-dom";
import FilterBase from "../../../components/filterJob/filter-base";
import {
  Box,
  Select,
  MenuItem,
  Grid,
  Typography,
  Button,
  CircularProgress,
  Tooltip,
  Modal,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CSVDownloadButton from "../../../components/downloadCSV";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import styles from "./style.module.css";
import { JobCards } from "./job-cards";
import Pagination from "@mui/material/Pagination";
import AdminApi from "../../../config/apis/admin";
import { api, matchingApi } from "../../../config/apis";
import { DataLoader, GetUserId } from "../../../helper/helperFunction";
import JobSingleCard from "./job-single-card";

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(1),
  right: theme.spacing(1),
}));

export const Jobs = () => {
  const userIsStudent = IsStudent();
  const userId = GetUserId();

  const [filters, setFilters] = useState();
  const [currentValue, setCurrentValue] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [meta, setMeta] = useState(null);

  const handleFiltersChange = (filterState) => {
    setFilters(filterState);
  };

  const handleChange = (event) => {
    const newValue = event.target.value;
    setCurrentValue(newValue);
  };

  const [jobData, setJobData] = useState();
  const [loading, setLoading] = useState(true);
  const [aiRecommendationsByJobId, setAiRecommendationsByJobId] = useState({});
  const [aiLoading, setAiLoading] = useState(false);
  const [aiLoaded, setAiLoaded] = useState(false);
  const [aiFiltersApplied, setAiFiltersApplied] = useState(false);
  const [aiFilterDialogOpen, setAiFilterDialogOpen] = useState(false);
  const [locationPriority, setLocationPriority] = useState(false);
  const [jobTypePriority, setJobTypePriority] = useState(false);

  const displayJobData = useMemo(() => {
    const jobs = Array.isArray(jobData) ? jobData : [];
    if (!userIsStudent || !aiLoaded || !jobs.length) return jobs;

    const indexed = jobs.map((job, idx) => ({ job, idx }));
    indexed.sort((a, b) => {
      const ra = aiRecommendationsByJobId?.[a.job?.id]?.rank;
      const rb = aiRecommendationsByJobId?.[b.job?.id]?.rank;
      if (ra == null && rb == null) return a.idx - b.idx;
      if (ra == null) return 1;
      if (rb == null) return -1;
      if (ra !== rb) return ra - rb;
      return a.idx - b.idx;
    });
    return indexed.map((x) => x.job);
  }, [aiLoaded, aiRecommendationsByJobId, jobData, userIsStudent]);

  useEffect(() => {
    let ignore = false;

    const fetchJobs = async () => {
      try {
        setLoading(true);
        if (userIsStudent) {
          setAiRecommendationsByJobId({});
          setAiLoaded(false);
        }

        const ApiUrl = userIsStudent
          ? AdminApi.JOBS_LIST_STUDENT
          : AdminApi.JOBS_LIST;
        const res = await api.get(ApiUrl, {
          params: {
            sort_order: currentValue,
            filters: filters,
            page: currentPage,
            limit: 10,
          },
        });

        if (ignore) return null;

        const data = Array.isArray(res?.data?.data) ? res.data.data : [];
        const nextMeta = res?.data?.meta || null;

        setJobData(data);
        setTotalPages(nextMeta?.totalPages || 1);
        setMeta(nextMeta);

        return null;
      } catch (error) {
        console.error(error);
        if (!ignore) {
          setJobData([]);
          setTotalPages(1);
          setMeta(null);
        }
        return null;
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    (async () => {
      await fetchJobs();
    })();

    return () => {
      ignore = true;
    };
  }, [currentValue, filters, currentPage, userIsStudent, userId]);

  const fetchAiRecommendations = async (locPriority, jobPriority) => {
    if (!userIsStudent || !userId) return;

    const jobs = Array.isArray(jobData) ? jobData : [];
    const itemCount = meta?.itemCount ?? jobs.length;
    const topN = Number(itemCount) || 0;
    if (topN <= 0) return;

    try {
      setAiLoading(true);
      setAiRecommendationsByJobId({});
      setAiLoaded(false);
      setAiFiltersApplied(false);

      const res = await matchingApi.get(
        `recommendations/students/${userId}/jobs`,
        {
          params: {
            top_n: topN,
            location_priority: locPriority,
            job_type_priority: jobPriority,
            include_inactive: false,
            skip_llm: false,
          },
        },
      );

      const list = Array.isArray(res?.data?.recommendations)
        ? res.data.recommendations
        : [];

      if (!list.length) {
        toast.info(res?.data?.message || "No recommended jobs found.", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "light",
        });
      }

      const nextMap = {};
      list.forEach((rec, index) => {
        if (rec?.job_id == null) return;
        nextMap[rec.job_id] = {
          rank: index + 1,
          final_score: rec?.final_score,
          missing_skills: rec?.missing_skills,
        };
      });

      const jobsOnPageSet = new Set(
        jobs.map((j) => j?.id).filter((id) => id != null),
      );
      const topRecOnPage = list.find((rec) => jobsOnPageSet.has(rec?.job_id));
      if (topRecOnPage?.job_id != null && nextMap[topRecOnPage.job_id]) {
        nextMap[topRecOnPage.job_id].is_top = true;
      }

      setAiRecommendationsByJobId(nextMap);
      setAiFiltersApplied(!!(locPriority || jobPriority));
      setAiLoaded(true);
    } catch (error) {
      console.error(error);
      setAiRecommendationsByJobId({});
      setAiFiltersApplied(false);
      setAiLoaded(false);
    } finally {
      setAiLoading(false);
    }
  };

  const handleOpenAiFilterDialog = async () => {
    if (!userIsStudent || !userId) return;
    if (loading || aiLoading || !Array.isArray(jobData)) return;

    try {
      setAiLoading(true);
      const statusRes = await matchingApi.get(`resumes/status/${userId}`);
      const status = statusRes?.data;
      if (!status?.is_ready) {
        const ps = status?.processing_status;
        let message;
        if (ps === "no_resume") {
          message = status?.message || "Please upload a resume first.";
        } else if (ps === "failed") {
          message = status?.message || "AI processing failed. Please try again.";
        } else {
          message = "AI is enhancing your resume for better matching..";
        }
        toast.info(message, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "light",
        });
        return;
      }

      setLocationPriority(false);
      setJobTypePriority(false);
      setAiFilterDialogOpen(true);
    } catch (error) {
      console.error(error);
    } finally {
      setAiLoading(false);
    }
  };

  const handleCloseAiFilterDialog = () => {
    setAiFilterDialogOpen(false);
  };

  const handleConfirmAiFilter = () => {
    const locPriority = locationPriority;
    const jobPriority = jobTypePriority;
    setAiFilterDialogOpen(false);
    fetchAiRecommendations(locPriority, jobPriority);
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  return (
    <>
      <Box className="container">
        <Box className="course-content">
          <h1 className="main-heading">Job Board</h1>

          <HasPermission permission="manage-jobs-board.create">
            <Box className="btn-wrapper">
              <Link to={"edit"}>Add a Job</Link>
            </Box>
          </HasPermission>
        </Box>
        <Box className={styles.display_left}>
          <FilterBase onFiltersChange={handleFiltersChange} />
          <Box className={styles.display_right}>
            <Box marginRight={2}>
              {!userIsStudent && (
                <CSVDownloadButton
                  apiEndpoint={AdminApi.JOBS_LIST}
                  fileName={"Jobs"}
                  params={currentValue}
                />
              )}
            </Box>
            <Box>
              {userIsStudent && (
                <Tooltip title="AI job recommendations for you" arrow>
                  <Button
                    variant="contained"
                    onClick={handleOpenAiFilterDialog}
                    disabled={loading || aiLoading || !Array.isArray(jobData)}
                    sx={{
                      mr: 2,
                      textTransform: "none",
                      backgroundColor: "#001C28",
                      "&:hover": { backgroundColor: "#00131c" },
                    }}
                  >
                    {aiLoading ? (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <CircularProgress size={16} sx={{ color: "#fff" }} />
                        Loading AI...
                      </Box>
                    ) : (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <AutoAwesomeIcon sx={{ fontSize: 18, color: "#fff" }} />
                        AI Recommendations
                      </Box>
                    )}
                  </Button>
                </Tooltip>
              )}
              <Select
                value={currentValue}
                onChange={handleChange}
                className="select-menu"
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </Box>
          </Box>
        </Box>
        {!userIsStudent && <JobCards />}

        <Grid container marginTop={2} spacing={2}>
          {loading ? (
            <DataLoader />
          ) : (
            displayJobData &&
            displayJobData.map((item) => {
              return (
                <JobSingleCard
                  item={item}
                  key={item?.id ?? `${item?.job_title || "job"}`}
                  aiEnabled={aiLoaded}
                  aiFiltersApplied={aiFiltersApplied}
                  recommendation={
                    userIsStudent ? aiRecommendationsByJobId?.[item?.id] : null
                  }
                />
              );
            })
          )}
        </Grid>

        <Modal
          open={aiFilterDialogOpen}
          onClose={handleCloseAiFilterDialog}
        >
          <Box className="popup" sx={{ maxWidth: 650 }}>
            <CloseButton onClick={handleCloseAiFilterDialog}>
              <CloseIcon />
            </CloseButton>

            <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
              AI Recommendations
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={locationPriority}
                      onChange={(e) => setLocationPriority(e.target.checked)}
                    />
                  }
                  label="Filter by location"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={jobTypePriority}
                      onChange={(e) => setJobTypePriority(e.target.checked)}
                    />
                  }
                  label="Filter by job type"
                />
              </Grid>

              <Grid item xs={12} className="btn-flex" sx={{ mt: 1 }}>
                <Button
                  className="btn cancel"
                  onClick={handleCloseAiFilterDialog}
                  sx={{ color: "#fff" }}
                >
                  Cancel
                </Button>
                <Button
                  className="btn save"
                  onClick={handleConfirmAiFilter}
                  sx={{ color: "#fff" }}
                >
                  Match
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Modal>

        <Grid
          container
          marginTop={2}
          spacing={2}
          justifyContent={"space-between"}
        >
          <Grid item xs={4}>
            <span className={"count"}>
              {`${
                (meta?.currentPage - 1) * meta?.itemsPerPage + 1
              } - ${Math.min(
                meta?.currentPage * meta?.itemsPerPage,
                meta?.totalItems,
              )} of ${meta?.totalItems}`}
            </span>
          </Grid>
          <Grid item xs={8} container justifyContent={"flex-end"}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              className={styles.pagination_wrap}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  );
};
